import { useMemo } from 'react';
import { Button } from '@mui/material';
import Card from '../../../../common/card';
import SelectMenu from '../../../../common/select-menu';
import l from '../../../../../lang';
import { FlowType, PayoutEnabledCountryConfiguration } from '../../../../../store/payouts/interfaces';
import { getPayoutCountryOptions, recordToSelectMenuOptions } from '../utils';
import { ReactComponent as WorldIcon } from '../../../../../assets/icons/world.svg';
import { ReactComponent as BriefcaseIcon } from '../../../../../assets/icons/briefcase.svg';

interface CountrySelectionProps {
  country: string;
  flowType: FlowType;
  enabledCountryConfigurations: PayoutEnabledCountryConfiguration[];
  loadingConfigurations: boolean;
  onCountryChange: (country: string) => void;
  onFlowTypeChange: (flowType: FlowType) => void;
  onCancel: () => void;
  onNext: () => void;
}

const CountrySelection = ({
  country,
  flowType,
  enabledCountryConfigurations,
  loadingConfigurations,
  onCountryChange,
  onFlowTypeChange,
  onCancel,
  onNext,
}: CountrySelectionProps) => {
  const countryOptions = useMemo(
    () => recordToSelectMenuOptions(getPayoutCountryOptions(enabledCountryConfigurations, flowType)),
    [enabledCountryConfigurations, flowType]
  );

  return (
    <div className="payout-create__layout">
      <Card>
        <div className="payout-create__card-content">
          <h2>{l('payouts.create.accountData.title')}</h2>
          <p>{l('payouts.create.accountData.subtitle')}</p>
          <div className="transaction_detail__title_section">
            <WorldIcon className="transaction_detail__title_section__icon" />
            {l('payouts.create.accountData.countrySection')}
          </div>
          <SelectMenu
            name="country"
            label={l('payouts.create.field.country')}
            defaultOptionText={l('payouts.create.field.countryPlaceholder')}
            value={country}
            optionsArray={countryOptions}
            onChange={event => onCountryChange(event.target.value as string)}
            disabled={loadingConfigurations}
          />
          <br />
          <br />
          <div className="transaction_detail__title_section">
            <BriefcaseIcon className="transaction_detail__title_section__icon" />
            {l('payouts.create.accountData.flowTypeSection')}
          </div>
          <div className="payout-create__flow-selector">
            <Button
              className="payout-create__flow-selector__button"
              variant={flowType === FlowType.B2C ? 'contained' : 'outlined'}
              onClick={() => onFlowTypeChange(FlowType.B2C)}
            >
              {l('payouts.create.flow.person')}
            </Button>
            <Button
              className="payout-create__flow-selector__button"
              variant={flowType === FlowType.B2B ? 'contained' : 'outlined'}
              onClick={() => onFlowTypeChange(FlowType.B2B)}
            >
              {l('payouts.create.flow.business')}
            </Button>
          </div>
          <div className="payout-create__flow-selector__description">
            {flowType === FlowType.B2C
              ? l('payouts.create.flow.personDescription')
              : l('payouts.create.flow.businessDescription')}
          </div>
          <div className="payout-create__buttons">
            <Button
              variant="contained"
              size="large"
              onClick={onCancel}
              sx={{ backgroundColor: '#E8E8E8', color: 'black', ':hover': { backgroundColor: '#D0D0D0' } }}
            >
              {l('common.cancel')}
            </Button>
            <Button variant="contained" size="large" onClick={onNext} disabled={!country || loadingConfigurations}>
              {l('common.next')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default CountrySelection;
